import React, { useState, useEffect } from 'react';
import { Star, Send, MapPin, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import Layout from '../components/layout/Layout';
import { toast } from 'sonner';
import { reviews as mockReviews, companyInfo, products } from '../data/mock';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ReviewsPage = () => {
  const [reviews, setReviews] = useState(mockReviews);
  const [showForm, setShowForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    rating: 5,
    text: '',
    product: ''
  });

  // Fetch approved reviews from API
  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await fetch(`${API_URL}/api/reviews?approved_only=true`);
        if (response.ok) {
          const data = await response.json();
          if (data.length > 0) {
            setReviews(data);
          }
        }
      } catch (error) {
        console.log('Using mock reviews');
      }
    };
    fetchReviews();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/reviews`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSubmitted(true);
        setFormData({
          name: '',
          location: '',
          rating: 5,
          text: '',
          product: ''
        });
        toast.success('Спасибо за отзыв! Он появится на сайте после модерации.');
      } else {
        throw new Error('Failed to submit review');
      }
    } catch (error) {
      toast.error('Ошибка отправки отзыва. Попробуйте позже.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Get unique product names for dropdown
  const productOptions = [...new Set(products.map(p => p.name))];

  return (
    <Layout>
      {/* Header */}
      <div className="bg-gradient-to-br from-emerald-50 to-green-50 py-10">
        <div className="container mx-auto px-4">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">Отзывы клиентов</h1>
          <p className="text-gray-600">Что говорят о нас покупатели из {companyInfo.regionRod}</p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        {/* Add Review Button */}
        <div className="text-center mb-10">
          <Button
            onClick={() => setShowForm(!showForm)}
            className="bg-emerald-600 hover:bg-emerald-700"
            size="lg"
          >
            <Send className="w-4 h-4 mr-2" />
            Оставить отзыв
          </Button>
        </div>

        {/* Review Form */}
        {showForm && !submitted && (
          <div className="max-w-2xl mx-auto mb-12 bg-white p-8 rounded-2xl shadow-lg border border-emerald-100">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Оставить отзыв</h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Ваше имя *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Иван Иванов"
                    required
                    className="mt-1"
                    data-testid="review-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="location">Город/населённый пункт *</Label>
                  <Input
                    id="location"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    placeholder="г. Туймазы"
                    required
                    className="mt-1"
                    data-testid="review-location-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="product">Какой товар приобрели?</Label>
                <Select
                  value={formData.product}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, product: value }))}
                >
                  <SelectTrigger className="mt-1" data-testid="review-product-select">
                    <SelectValue placeholder="Выберите теплицу" />
                  </SelectTrigger>
                  <SelectContent>
                    {productOptions.map(product => (
                      <SelectItem key={product} value={product}>{product}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Оценка *</Label>
                <div className="flex gap-2 mt-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, rating: star }))}
                      className="focus:outline-none"
                      data-testid={`review-star-${star}`}
                    >
                      <Star
                        className={`w-8 h-8 transition-colors ${
                          star <= formData.rating
                            ? 'fill-amber-400 text-amber-400'
                            : 'text-gray-300 hover:text-amber-200'
                        }`}
                      />
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label htmlFor="text">Ваш отзыв *</Label>
                <Textarea
                  id="text"
                  name="text"
                  value={formData.text}
                  onChange={handleInputChange}
                  placeholder="Расскажите о вашем опыте..."
                  required
                  rows={5}
                  className="mt-1"
                  data-testid="review-text-input"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-emerald-600 hover:bg-emerald-700"
                disabled={isSubmitting}
                data-testid="review-submit-btn"
              >
                {isSubmitting ? 'Отправка...' : 'Отправить отзыв'}
              </Button>

              <p className="text-sm text-gray-500 text-center">
                Отзыв будет опубликован после проверки модератором
              </p>
            </form>
          </div>
        )}

        {/* Success Message */}
        {submitted && (
          <div className="max-w-2xl mx-auto mb-12 bg-emerald-50 p-8 rounded-2xl text-center">
            <CheckCircle className="w-16 h-16 text-emerald-600 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">Спасибо за отзыв!</h3>
            <p className="text-gray-600 mb-4">
              Ваш отзыв отправлен на модерацию и появится на сайте после проверки.
            </p>
            <Button
              variant="outline"
              onClick={() => {
                setSubmitted(false);
                setShowForm(false);
              }}
            >
              Закрыть
            </Button>
          </div>
        )}

        {/* Reviews Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reviews.map(review => (
            <div
              key={review.id}
              className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
            >
              <div className="flex items-center gap-1 mb-3">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className={`w-4 h-4 ${
                      i < review.rating ? 'fill-amber-400 text-amber-400' : 'text-gray-300'
                    }`}
                  />
                ))}
              </div>
              
              <p className="text-gray-600 mb-4">{review.text}</p>
              
              {review.product && (
                <p className="text-sm text-emerald-600 mb-3">{review.product}</p>
              )}
              
              <div className="pt-4 border-t border-gray-100">
                <p className="font-semibold text-gray-900">{review.name}</p>
                <p className="text-sm text-gray-500 flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {review.location}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
};

export default ReviewsPage;
